import React, { useState, useEffect, useMemo } from 'react';
import {
  Flex,
  Typography,
  spacing,
  useI18nContext,
  colors,
} from '@procore/core-react';
import styled from 'styled-components';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useIntersection } from '../../../../../hooks/useIntersection';
import { marketplaceColors } from '../../../../../styles/constants';

const HEADER_HEIGHT = 72;
const HEADER_HEIGHT_MOBILE = 120;

export const ScrollAnchor = styled.div`
  scroll-margin-top: 140px;
  @media (max-width: 992px) {
    scroll-margin-top: 190px;
  }
`;

const LocalTab = styled.div`
  padding: ${spacing.sm}px;
  cursor: pointer;
  transition: all 0.15s ease;
  background-color: white;
  &:hover {
    background-color: ${marketplaceColors.stone40};
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }
  border-bottom: 3px solid ${({ active }) => (active ? 'black' : colors.gray85)};
  margin-bottom: -3px;
  ${({ active }) => active && 'font-weight: semibold;'}
`;

const NavContainer = styled.div`
  position: sticky;
  top: ${HEADER_HEIGHT}px;
  z-index: 9995;
  width: 100%;
  max-width: 100%;
  background-color: white;
  @media (max-width: 992px) {
    top: ${HEADER_HEIGHT_MOBILE}px;
    width: 100vw;
  }

  overflow-x: auto;
  overflow-y: hidden;
  /* Hide scrollbar for WebKit browsers */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for Firefox */
  scrollbar-width: none;
`;

const BorderBox = styled.div`
  border-bottom: 3px solid ${colors.gray85};
  width: 100%;
  @media (max-width: 700px) {
    width: max-content;
  }
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FlexContainer = styled.div`
  width: 100%;
  max-width: 1344px;
`;

const MediaResponsiveFlex = styled(Flex)`
  @media (max-width: 768px) {
    gap: ${spacing.sm}px;
  }
  gap: ${spacing.lg}px;
  background-color: white;
  width: max-content;
`;

export const SectionNav = ({ sections }) => {
  const i18n = useI18nContext();
  const sectionRefs = useMemo(
    () => sections.map((section) => section.ref),
    [sections]
  );
  const [overRideActiveSection, setOverRideActiveSection] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const lastIntersection = useIntersection(sectionRefs);
  useEffect(() => {
    if (!overRideActiveSection) {
      setActiveSection(() => lastIntersection);
    }
  }, [lastIntersection]);

  return (
    <NavContainer>
      <BorderBox>
        <FlexContainer>
          <MediaResponsiveFlex>
            {sections.map((section, index) => (
              <LocalTab
                key={section.id}
                className={classNames({ active: index === 0 })}
                active={activeSection === section.id}
                onClick={() => {
                  section.ref.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  });
                  setOverRideActiveSection(section.id);
                  setActiveSection(section.id);
                  setTimeout(() => {
                    setOverRideActiveSection(null);
                  }, 800);
                }}
                data-testid={`section-nav-${section.id}`}
              >
                <Typography
                  className={`font-size-body${activeSection !== section.id ? ' color-concrete' : ''}`}
                  style={{
                    padding: `${spacing.sm}px`,
                  }}
                  weight={activeSection === section.id ? 'semibold' : 'regular'}
                >
                  {i18n.t(section.title)}
                </Typography>
              </LocalTab>
            ))}
          </MediaResponsiveFlex>
        </FlexContainer>
      </BorderBox>
    </NavContainer>
  );
};

SectionNav.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      ref: PropTypes.object.isRequired,
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
};
